import {produce} from 'immer';
import React, {useMemo} from 'react';
import {ContentTitle} from 'src/design/atoms/Typography';
import {FormField} from 'src/app/forms/FormField';
import {noYesBooleanOptions} from 'src/shared/constants/fieldTypeDictionary';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  useGetFormSettings,
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

const generateFailureEventField: IFormFieldV2 = {
  name: 'isFailureTriggerField',
  label: 'Should this field trigger a failure?',
  type: 'select',
  options: noYesBooleanOptions,
};

export const FailureSettings = React.memo(() => {
  const formSettings = useGetFormSettings();
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();

  const failureEventValueOptions: ISelectOption[] = useMemo(
    () =>
      field
        ? [
            {label: field?.switchOptions?.positiveText ?? 'Yes', id: 'true'},
            {label: field?.switchOptions?.negativeText ?? 'No', id: 'false'},
          ]
        : [],
    [field?.switchOptions?.positiveText, field?.switchOptions?.negativeText]
  );

  const eventTriggerField: IFormFieldV2 = useMemo(
    () => ({
      name: 'failureTriggerValue',
      label: 'Trigger failure when field is',
      type: 'select',
      options: failureEventValueOptions,
      disabled: field?.builderOptions?.isFailureTriggerField !== true,
    }),
    [field?.builderOptions?.isFailureTriggerField, failureEventValueOptions]
  );

  const handleIsFailureTriggerFieldChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.builderOptions = {
            ...draft.builderOptions,
            isFailureTriggerField: value === 'true',
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  const handleFailureEventTriggerValueChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.builderOptions = {
            ...draft.builderOptions,
            failureTriggerValue: value,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  if (!formSettings.failable || !field || field.type !== 'switch')
    return null;

  return (
    <>
      <ContentTitle text="Failure Options" />
      <FormField
        field={generateFailureEventField}
        value={
          field?.builderOptions?.isFailureTriggerField === true
            ? 'true'
            : 'false'
        }
        onChange={handleIsFailureTriggerFieldChange}
      />

      <FormField
        field={eventTriggerField}
        value={field?.builderOptions?.failureTriggerValue}
        onChange={handleFailureEventTriggerValueChange}
      />
    </>
  );
});
